import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FaArrowLeft, FaPen, FaPaperPlane, FaCheck, FaTimes, FaPrint, FaTrash,
  FaCheckCircle, FaHourglassHalf,
  FaInfoCircle, FaBan, FaUndo, FaCopy, FaFolderOpen,
} from "react-icons/fa";
import {
  getPaymentRequest, submitPaymentRequest, withdrawPaymentRequest, deletePaymentRequest, clearPaymentRequest, openPaymentRequestPdf,
  duplicatePaymentRequest, getInvoiceDetail,
} from "redux/erp/paymentRequestSlice";
import { FiSidebar } from "react-icons/fi";
import { Button, LoadingSpinner, Modal } from 'components/common';
import useBreadcrumb from "hooks/useBreadcrumb";
import PageShell from "./common/PageShell";
import { formatDate } from "./common/fileMeta";
import Clickable from "./common/Clickable";
import ApproveForm from "./detail/ApproveForm";
import ApprovalPrerequisites, { approvalBlockers } from './detail/ApprovalPrerequisites';
import RejectForm from "./detail/RejectForm";
import CancelForm from "./detail/CancelForm";
import InvoicePreviewFrame from "./common/InvoicePreviewFrame";
import PdfOverlay from "./common/PdfOverlay";
import DocumentsTable from "./common/DocumentsTable";
import AttachmentsPanel from "./common/AttachmentsPanel";
import FinalSignerCard from "./detail/FinalSignerCard";
import IntegrationsCard from "./detail/IntegrationsCard";
import PurchaseOrderCard from "./detail/PurchaseOrderCard";
import ApprovalProgress from "./detail/ApprovalProgress";
import DisbursementAction from "./detail/DisbursementAction";
import PaymentProcessingBanner from "./detail/PaymentProcessingBanner";
import MarkPaidModal from "./detail/MarkPaidModal";
import JournalWorkbench from "./detail/JournalWorkbench";
import HsbcPackageBanner from "./detail/HsbcPackageBanner";
import ActivityTimeline from "./detail/ActivityTimeline";
import AccountingAttachments from "./detail/AccountingAttachments";
import FinanceCompletionCard from './detail/FinanceCompletionCard';
import { journalTabBadge } from "./detail/journalTabBadge";
import SmartCaSignCard from "./detail/SmartCaSignCard";
import { PriorityPill } from "./common/priority";
import { StatusPill } from "./common/status";
import { formatIsoDay } from "./common/IsoDateInput";
import SplitPane from "./common/SplitPane";
import { requestCurrencyOf, formatCurrency } from "./common/lineCurrency";
import "./PaymentRequestDetail.css";

const money = (v) => (Number(v) || 0).toLocaleString("en-US");
// Một phiếu = một loại tiền (lineCurrency): bảng chỉ cần một cột số tiền.
const lineAmount = (item, currency) => (currency === "USD" ? item.amount_usd : item.amount_vnd);

const FORM_LABEL = {
  qf01: "Payment Request — QF-FNC-01",
  qf02: "Advance Payment — QF-FNC-02",
  qf03: "Clear Downpayment — QF-FNC-03",
};



// ── UI primitives ────────────────────────────────────────────────────────────
const Info = ({ label, children, fullWidth = false }) => (
  <div className={`tw-flex tw-flex-col tw-py-1.5 ${fullWidth ? "md:tw-col-span-2" : ""}`}>
    <span className="tw-text-[10px] tw-font-bold tw-text-geist-fg-3 tw-uppercase tw-tracking-wider">{label}</span>
    <span className="tw-text-sm tw-text-geist-fg tw-mt-0.5 tw-break-words">{children || <span className="tw-text-geist-fg-3">—</span>}</span>
  </div>
);

Info.propTypes = {
  label: PropTypes.node,
  children: PropTypes.node,
  fullWidth: PropTypes.bool,
};

const TH = "tw-px-4 tw-py-2.5 tw-text-left tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-2 tw-whitespace-nowrap tw-bg-geist-bg-2";
// 12px like the Purchase Order card below; dates, invoice numbers and money stay
// on one line (NUM) so only the description wraps.
const TD = "tw-px-4 tw-py-2 tw-text-[12px] tw-leading-5 tw-text-geist-fg tw-align-top";
const NUM = "tw-text-right tw-tabular-nums tw-whitespace-nowrap";

const PaperStat = ({ label, value, tone = "neutral" }) => {
  const toneMap = {
    neutral: "tw-text-geist-fg",
    danger:  "tw-text-geist-error",
    success: "tw-text-geist-success",
  };
  return (
    <div className="tw-px-3 tw-py-3 tw-text-center">
      <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-[0.1em] tw-text-geist-fg-2 tw-mb-1">{label}</p>
      <p className={`tw-text-base tw-font-bold tw-tabular-nums tw-mb-0 ${toneMap[tone]}`}>{money(value)}</p>
    </div>
  );
};

PaperStat.propTypes = {
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tone: PropTypes.oneOf(["neutral", "danger", "success"]),
};

// ─────────────────────────────────────────────────────────────────────────────
const PaymentRequestDetail = () => {
  const { id }     = useParams();
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { recordData: pr } = useSelector((s) => s.erp_payment_request);

  const [showApprove, setShowApprove] = useState(false);
  const [showFinanceDocuments, setShowFinanceDocuments] = useState(false);
  const [showReject,  setShowReject]  = useState(false);
  const [showCancel,  setShowCancel]  = useState(false);
  const [showDelete,  setShowDelete]  = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  // One header action at a time: the running one shows a spinner, the others are locked,
  // and a second click before React re-renders is ignored (no double submit / duplicate).
  const [busyAction, setBusyAction] = useState(null);
  const busyRef = useRef(null);
  const runAction = useCallback(async (name, fn) => {
    if (busyRef.current) return;
    busyRef.current = name;
    setBusyAction(name);
    try { await fn(); } finally { busyRef.current = null; setBusyAction(null); }
  }, []);
  // Tab giữ ở đây (deep link prepareJournal mở thẳng tab journal từ header).
  const [tab, setTab] = useState("request");
  const [approvalExpanded, setApprovalExpanded] = useState(true);

  // Xem lại PDF hoá đơn ngay trên trang: trước đây item có
  // doc_platform_document_id nhưng bảng không render ra chỗ nào để mở lại file.
  const [previewDocId,   setPreviewDocId]   = useState(null);
  const [previewUrl,     setPreviewUrl]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfOpen,        setPdfOpen]        = useState(false);
  const urlCache = useRef({});

  // Shown only once THIS visit has read the request: a copy left in the persisted store
  // carries someone else's permissions (another identity, or before a change) and drew
  // panels this user may not open — a 403 page instead of the request (2026-10-22).
  const [loadedId, setLoadedId] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoadedId(null);
    Promise.resolve(dispatch(getPaymentRequest(id))).finally(() => { if (alive) setLoadedId(String(id)); });
    return () => { alive = false; dispatch(clearPaymentRequest()); };
  }, [dispatch, id]);

  const releaseState = pr?.bank?.automatic_release?.state;
  useEffect(() => {
    if (!['queued', 'dispatching'].includes(releaseState)) return undefined;
    const timer = setInterval(() => dispatch(getPaymentRequest(id)), 5000);
    return () => clearInterval(timer);
  }, [dispatch, id, releaseState]);

  const showPreview = useCallback(async (documentId) => {
    if (!documentId) return;
    setPreviewDocId(documentId);

    if (urlCache.current[documentId]) {
      setPreviewUrl(urlCache.current[documentId]);
      return;
    }
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const inv = await dispatch(getInvoiceDetail(documentId)).unwrap();
      const url = inv?.view_url || inv?.download_url || null;
      if (url) urlCache.current[documentId] = url;
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [dispatch]);

  // Breadcrumb động: hiện mã phiếu thay vì id.
  useBreadcrumb(
    pr && pr.id
      ? [
          { label: "Payment Requests", to: "/payment-requests" },
          { label: pr.code || `PR ${pr.id}` },
        ]
      : null
  );

  // Spinner toàn trang chỉ khi chưa có đúng phiếu này (lần đầu / đổi phiếu). Tải lại
  // cùng phiếu (sau create journal lỗi, polling auto-release...) giữ trang mounted,
  // nếu không JournalWorkbench mất tab API/APP đang mở và các dòng đang sửa dở.
  const hasRecord = !!pr?.id && String(pr.id) === String(id) && loadedId === String(id);
  if (!hasRecord) {
    return <PageShell header={<div className="tw-px-6 tw-py-4" />}><LoadingSpinner message="Loading..." /></PageShell>;
  }

  const perms    = pr.permissions || {};
  const currency = requestCurrencyOf(pr.items || []);
  const approvalBlocked = approvalBlockers(pr).length > 0;
  const prepareJournal = () => { setShowApprove(false); setTab('journal'); };
  const activeInvoiceNo = previewDocId
    ? (pr.items || []).find((i) => i.doc_platform_document_id === previewDocId)?.inv_no
    : null;
  const statusId = pr.status?.id ?? pr.status;

  // Permission helpers — "once signed, edit/delete lock for the owner"
  // - allow_edit / allow_delete from BE: owner + draft
  // - allow_reject from BE: pending + current_signer (approver rejects their step — terminal, status Rejected)
  // - allow_cancel from BE: owner + (draft or pending) (requestor cancels outright — terminal, status Canceled)
  const hasAnySigned = (pr.approvals || []).some(a => a.is_approved || a.is_rejected);
  const canEditOwner   = perms.allow_edit   && !hasAnySigned;
  const canDeleteOwner = perms.allow_delete && !hasAnySigned;

  const doSubmit = () => runAction("submit", async () => {
    try {
      await dispatch(submitPaymentRequest(pr.id)).unwrap();
      await dispatch(getPaymentRequest(pr.id));
      toast.success("Submitted for approval.");
    } catch (e) { toast.error(e?.error || e?.message || "Submit failed."); }
  });
  // HSBC: the instruction is sent from ERP & bank — on the ERP journals tab for Accounting.
  const canSendHsbc = pr.disbursement?.status === "processing" && pr.bank?.route === "hsbc"
    && pr.bank?.status === "not_sent" && !!perms.allow_change_bank_route;
  const openBankCard = () => {
    if (perms.allow_view_journal) setTab("journal");
    window.requestAnimationFrame(() => document.getElementById("payment-request-bank-card")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  // Rút phiếu về nháp để bổ sung — chỉ khi Manager CHƯA ký (BE chặn nếu đã ký).
  const doWithdraw = () => runAction("withdraw", async () => {
    try {
      await dispatch(withdrawPaymentRequest(pr.id)).unwrap();
      await dispatch(getPaymentRequest(pr.id));
      toast.success("Withdrawn — the request is back to draft for editing.");
    } catch (e) { toast.error(e?.error || e?.message || "Withdraw failed."); }
  });
  const doDelete = () => runAction("delete", async () => {
    try {
      await dispatch(deletePaymentRequest(pr.id)).unwrap();
      toast.success("Deleted.");
      navigate("/payment-requests");
    } catch (e) { toast.error(e?.error || e?.message || "Delete failed."); }
    finally { setShowDelete(false); }
  });

  // Mở bản in PDF theo mẫu (QF-FNC) do backend render (kèm chữ ký đã ký digital).
  const doPrint = () => runAction("print", async () => {
    try {
      await openPaymentRequestPdf(pr.id);
    } catch (e) { toast.error(e?.message || "Could not open the printable form."); }
  });

  // Duplicate — tạo 1 phiếu mới ở Draft, copy nội dung từ phiếu này.
  const doDuplicate = () => runAction("duplicate", async () => {
    try {
      const res = await dispatch(duplicatePaymentRequest(pr.id)).unwrap();
      const newPr = res?.data?.attributes || res?.data || {};
      toast.success(`Duplicated into new draft ${newPr.code || ""}.`);
      navigate(`/payment-requests/${newPr.id}`);
    } catch (e) { toast.error(e?.error || e?.message || "Duplicate failed."); }
  });
  const locked = !!busyAction;

  const header = (
    <div className="tw-px-4 sm:tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
      <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
        <Clickable onClick={() => navigate("/payment-requests")} className="tw-text-geist-fg-3 hover:tw-text-geist-fg tw-transition-colors">
          <FaArrowLeft size={14} />
        </Clickable>
        <div className="tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <h1 className="tw-text-lg tw-font-semibold tw-tracking-tight tw-leading-tight tw-text-geist-fg tw-m-0">{pr.code}</h1>
            <StatusPill status={statusId} />
            <PriorityPill value={pr.priority} suffix=" priority" />
          </div>
          <p className="tw-text-xs tw-text-geist-fg-2 tw-m-0">{FORM_LABEL[pr.form_type] || FORM_LABEL.qf01}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="payment-request-toolbar" role="toolbar" aria-label="Payment request actions">
        <button type="button" aria-label="Finance workflow & documents" title="Finance workflow & documents"
          aria-haspopup="dialog" onClick={() => setShowFinanceDocuments(true)}
          className="payment-request-toolbar-icon">
          <FaFolderOpen aria-hidden="true" size={14} />
        </button>
        <button type="button" aria-expanded={approvalExpanded} aria-controls="payment-approval-progress"
          aria-label={approvalExpanded ? 'Hide approval progress' : 'Show approval progress'}
          title={approvalExpanded ? 'Hide approval progress' : 'Show approval progress'}
          onClick={() => setApprovalExpanded((expanded) => !expanded)} className="payment-request-toolbar-icon">
          <FiSidebar aria-hidden="true" size={14} style={{ transform: 'scaleX(-1)' }} />
        </button>
        {canEditOwner && (
          <Button variant="secondary" size="md" leftIcon={FaPen} disabled={locked} onClick={() => navigate(`/payment-requests/${pr.id}/edit`)}>
            Edit
          </Button>
        )}
        {perms.allow_submit && (
          <Button variant="primary" size="md" leftIcon={FaPaperPlane} onClick={doSubmit}
            loading={busyAction === "submit"} disabled={locked} aria-busy={busyAction === "submit"}>
            {busyAction === "submit" ? "Submitting…" : "Submit"}
          </Button>
        )}
        {perms.allow_withdraw && (
          <Button variant="secondary" size="md" leftIcon={FaUndo} onClick={doWithdraw}
            loading={busyAction === "withdraw"} disabled={locked} aria-busy={busyAction === "withdraw"}
            title="Withdraw to draft for editing (only before the manager signs)">
            {busyAction === "withdraw" ? "Withdrawing…" : <>Withdraw &amp; edit</>}
          </Button>
        )}
        {perms.allow_approve && (
          <Button variant="success" size="md" leftIcon={FaCheck} disabled={approvalBlocked || locked} onClick={() => setShowApprove(true)}>
            Sign &amp; Approve
          </Button>
        )}
        {perms.allow_mark_disbursement_paid && (
          <Button variant="success" size="md" leftIcon={FaCheckCircle} disabled={locked} onClick={() => setShowMarkPaid(true)}>
            Mark as paid
          </Button>
        )}
        {perms.allow_reject && (
          <Button variant="secondary" className="payment-request-toolbar-danger" size="md" leftIcon={FaTimes} disabled={locked} onClick={() => setShowReject(true)}>
            Reject
          </Button>
        )}
        {perms.allow_cancel && (
          <Button variant="secondary" className="payment-request-toolbar-danger" size="md" leftIcon={FaBan} disabled={locked} onClick={() => setShowCancel(true)}
            title="Cancel this request permanently (different from Withdraw)">
            Cancel
          </Button>
        )}
        {perms.allow_duplicate && (
          <Button variant="secondary" size="md" leftIcon={FaCopy} onClick={doDuplicate}
            loading={busyAction === "duplicate"} disabled={locked} aria-busy={busyAction === "duplicate"}
            title="Create a new draft with this request's content">
            {busyAction === "duplicate" ? "Duplicating…" : "Duplicate"}
          </Button>
        )}
        {perms.allow_print && (
          <Button variant="secondary" size="md" leftIcon={FaPrint} onClick={doPrint}
            loading={busyAction === "print"} disabled={locked} aria-busy={busyAction === "print"}>
            {busyAction === "print" ? "Opening…" : "Print"}
          </Button>
        )}
        {canDeleteOwner && (
          <Button variant="secondary" size="md" className="payment-request-toolbar-icon payment-request-toolbar-danger" aria-label="Delete draft" leftIcon={FaTrash} disabled={locked} onClick={() => setShowDelete(true)} title="Delete draft" />
        )}
      </div>
    </div>
  );

  return (
    <PageShell header={header}>
      <div className="tw-p-4 sm:tw-p-6 tw-bg-geist-bg-2 tw-min-h-full">

        {/* Banners */}
        {statusId === 1 && pr.current_approver_email && (
          <div className="tw-mb-4 tw-rounded-geist-sm tw-border tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-px-4 tw-py-2.5 tw-flex tw-items-start tw-gap-2.5">
            <FaHourglassHalf className="tw-text-[#b46a00] tw-mt-0.5 tw-shrink-0" size={12} />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-[#8a5200] tw-mb-0">Awaiting signature</p>
              <p className="tw-text-xs tw-text-[#b46a00] tw-mb-0 tw-mt-0.5">
                Next approver: <strong>{pr.current_approver_name || pr.current_approver_email}</strong>
                {pr.current_approver_position && <span className="tw-text-[#b46a00]"> · {pr.current_approver_position}</span>}
              </p>
            </div>
          </div>
        )}

        {/* After BOD: the Payment Accountant's step (Processing → Paid), with its action. */}
        <PaymentProcessingBanner disbursement={pr.disbursement} canMarkPaid={!!perms.allow_mark_disbursement_paid}
          onMarkPaid={() => setShowMarkPaid(true)} hsbc={canSendHsbc} onOpenBank={openBankCard} />

        {statusId === 3 && (
          <div className="tw-mb-4 tw-rounded-geist-sm tw-border tw-border-[#f6cfcc] tw-bg-[#fdeceb] tw-px-4 tw-py-2.5 tw-flex tw-items-start tw-gap-2.5">
            <FaTimes className="tw-text-geist-error tw-mt-0.5 tw-shrink-0" size={12} />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-[#a32b25] tw-mb-0">
                Rejected at step {pr.cancelled_step}{pr.cancelled_role ? ` — ${pr.cancelled_role}` : ""}
              </p>
              <p className="tw-text-xs tw-text-geist-error tw-mb-0 tw-mt-0.5">
                This request is closed.{pr.cancel_reason ? <> Reason: <strong>{pr.cancel_reason}</strong>.</> : null} Use Duplicate to reuse its content in a new request.
              </p>
            </div>
          </div>
        )}

        {statusId === 4 && (
          <div className="tw-mb-4 tw-rounded-geist-sm tw-border tw-border-geist-border tw-bg-geist-bg-2 tw-px-4 tw-py-2.5 tw-flex tw-items-start tw-gap-2.5">
            <FaBan className="tw-text-geist-fg-3 tw-mt-0.5 tw-shrink-0" size={12} />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-geist-fg tw-mb-0">Canceled by requestor</p>
              <p className="tw-text-xs tw-text-geist-fg-2 tw-mb-0 tw-mt-0.5">
                This request is closed.{pr.cancel_reason ? <> Reason: <strong>{pr.cancel_reason}</strong>.</> : null} Use Duplicate to reuse its content in a new request.
              </p>
            </div>
          </div>
        )}

        {hasAnySigned && (canEditOwner === false && perms.allow_edit) && (
          <div className="tw-mb-4 tw-rounded-geist-sm tw-border tw-border-geist-border tw-bg-geist-bg-2 tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-geist-fg-2">
            <FaInfoCircle className="tw-text-geist-fg-3 tw-shrink-0" size={11} />
            This request has been signed by an approver. Edit and delete are locked — only the next approver can reject or cancel it.
          </div>
        )}

        {/* Hai cột kéo giãn được (SplitPane — cùng kiểu với form tạo phiếu). Nút sidebar
            trên header và nút ‹/› trên tay nắm cùng điều khiển approvalExpanded. */}
        <SplitPane storageKey="erp.payment-request-detail.split.v1" defaultLeft={66} min={45} max={80}
          rightLabel="approval progress" rightId="payment-approval-progress" small="stack"
          collapsed={!approvalExpanded} onCollapsedChange={(c) => setApprovalExpanded(!c)}
          left={(
          /* Left column — app-style cards */
          <div className="tw-min-w-0 tw-flex tw-flex-col tw-gap-4 lg:tw-pr-0">
            {/* Head of Finance / BOD: SmartCA signature before approving (send again once it expires). */}
            {perms.allow_approve && <SmartCaSignCard paymentRequest={pr} />}
            {perms.allow_approve && <ApprovalPrerequisites paymentRequest={pr}
              onPrepareJournal={perms.allow_refill_journal ? prepareJournal : undefined} />}
            {/* HSBC online payment: is the reviewed file still what is signed and paid? */}
            {perms.allow_finance_insight && <HsbcPackageBanner paymentRequest={pr} />}
            {/* Tab "ERP journals" — Kế toán, Kế toán trưởng, Head of Finance, BOD, admin
                (allow_view_journal). Chỉ người điền bổ sung được mới sửa: workbench tự
                khoá ô theo permissions.allow_refill của từng journal. */}
            {perms.allow_view_journal && (
              <div className="tw-inline-flex tw-self-start tw-items-center tw-gap-1 tw-p-1 tw-rounded-geist-md tw-border tw-border-solid tw-border-primary-200 tw-bg-primary-50" role="tablist">
                {[
                  { key: "request", label: "Request information", icon: "bi-file-earmark-text" },
                  // Where Accounting creates the request's journals in ERP before approving.
                  { key: "journal", label: "ERP journals", icon: "bi-journal-text",
                    badge: journalTabBadge(pr, { working: perms.allow_refill_journal }) },
                ].map((t) => (
                  <Clickable key={t.key} onClick={() => setTab(t.key)} role="tab" aria-selected={tab === t.key}
                    className={`tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-rounded-geist-sm tw-transition ${
                      tab === t.key ? "tw-bg-primary tw-text-white tw-shadow-geist-card" : "tw-text-primary-700 hover:tw-bg-primary-100"}`}>
                    <i className={`bi ${t.icon} tw-text-[11px]`} /> {t.label}
                    {t.badge && (
                      <span aria-label={`ERP journals: ${t.badge.text}`}
                        className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${
                          t.badge.tone === "ok" ? "tw-bg-[#ecfdf3] tw-text-[#067647]" : "tw-bg-[#fff8ee] tw-text-[#a05e03]"}`}>
                        {t.badge.tone === "ok" && <i className="bi bi-check2" />}{t.badge.text}
                      </span>
                    )}
                  </Clickable>
                ))}
              </div>
            )}

            {perms.allow_view_journal && tab === "journal" ? (
              <>
                <JournalWorkbench paymentRequest={pr} />
                {/* ERP & ngân hàng nằm cạnh bút toán: Kế toán gửi lại HSBC ngay tại tab này. */}
                <IntegrationsCard paymentRequest={pr} />
                {/* KTT xem bút toán xong chọn BOD ngay tại đây, không phải quay về tab Request. */}
                {perms.allow_set_final_signer && <FinalSignerCard paymentRequest={pr} />}
              </>
            ) : (<>
            {/* Amount summary */}
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-px-5 tw-py-4 tw-flex tw-items-start tw-justify-between tw-gap-4">
              <div className="tw-min-w-0">
                <p className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-mb-1">Total payable</p>
                <p className="tw-text-2xl tw-font-semibold tw-text-geist-fg tw-tabular-nums tw-m-0 tw-leading-none">
                  {formatCurrency(pr.total_amount, currency)}
                </p>
                {pr.amount_in_words && <p className="tw-text-[12px] tw-italic tw-text-geist-fg-3 tw-mt-2 tw-mb-0">{pr.amount_in_words}</p>}
              </div>
              <span className="tw-shrink-0 tw-text-[11px] tw-text-geist-fg-3 tw-bg-geist-bg-2 tw-border tw-border-solid tw-border-geist-border tw-rounded-xl tw-px-2.5 tw-py-1 tw-font-medium">
                {FORM_LABEL[pr.form_type] || FORM_LABEL.qf01}
              </span>
            </div>

            {/* Request details */}
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
              <div className="tw-px-5 tw-py-2.5 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-receipt tw-text-geist-fg-3" />
                <h3 className="tw-text-[13px] tw-font-semibold tw-text-geist-fg tw-m-0">Request details</h3>
              </div>
              <div className="tw-p-5 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4">
                <Info label="Payee">{pr.payee}</Info>
                <Info label="Employee code">{pr.employee_code}</Info>
                <Info label="Department">{pr.department}</Info>
                <Info label="Payment method">{pr.payment_method}</Info>
                <Info label="Bank">{pr.bank_name}</Info>
                <Info label="Account">{pr.bank_account}</Info>
                <Info label="Request date">{formatIsoDay(pr.request_date)}</Info>
                <Info label="Due date">{pr.due_date ? formatIsoDay(pr.due_date) : null}</Info>
              </div>
            </div>

            {/* Invoice details */}
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
              <div className="tw-px-5 tw-py-2.5 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-table tw-text-geist-fg-3" />
                <h3 className="tw-text-[13px] tw-font-semibold tw-text-geist-fg tw-m-0">Invoice details</h3>
              </div>
              {/* Ten columns do not survive a phone: the row squeezed the
                  description into a five-line sliver and still pushed Total off
                  the card, leaving an approver swiping sideways through money
                  figures. Below `md` each line is its own block instead. */}
              <div className="md:tw-hidden tw-divide-y tw-divide-solid tw-divide-geist-border">
                {(pr.items || []).length === 0 ? (
                  <p className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-geist-fg-3 tw-italic tw-m-0">No invoice lines.</p>
                ) : pr.items.map((i) => (
                  <div key={i.id} className="tw-px-4 tw-py-3">
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                      <div className="tw-min-w-0">
                        <div className="tw-text-[13px] tw-font-semibold tw-text-geist-fg">{i.inv_no || '—'}</div>
                        <div className="tw-text-[11px] tw-text-geist-fg-3">{i.inv_date}</div>
                      </div>
                      {i.doc_platform_document_id && (
                        <Clickable
                          onClick={() => showPreview(i.doc_platform_document_id)}
                          title={`View invoice PDF${i.inv_no ? ` ${i.inv_no}` : ""}`}
                          className="tw-inline-flex tw-shrink-0 tw-text-geist-fg-3 hover:tw-text-geist-accent tw-transition-colors"
                        >
                          <i className="bi bi-file-earmark-pdf tw-text-[15px]" />
                        </Clickable>
                      )}
                    </div>

                    {i.description && (
                      <p className="tw-mt-1.5 tw-mb-0 tw-text-[12px] tw-text-geist-fg-2">{i.description}</p>
                    )}
                    {i.cost_center && (
                      <p className="tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-geist-fg-3">Cost center: {i.cost_center}</p>
                    )}

                    <dl className="tw-mt-2 tw-mb-0 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1">
                      {[['Amount', formatCurrency(lineAmount(i, currency), currency)], ['VAT', formatCurrency(i.vat, currency)], ['Total', formatCurrency(i.total, currency)]]
                        .map(([label, value]) => (
                          <div key={label} className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
                            <dt className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-geist-fg-3">{label}</dt>
                            <dd className={`tw-m-0 tw-text-[12px] tw-tabular-nums ${label === 'Total' ? 'tw-font-semibold tw-text-geist-fg' : 'tw-text-geist-fg-2'}`}>
                              {value}
                            </dd>
                          </div>
                        ))}
                    </dl>

                    {i.note && <p className="tw-mt-1.5 tw-mb-0 tw-text-[11px] tw-text-geist-fg-3">{i.note}</p>}
                  </div>
                ))}

                <div className="tw-px-4 tw-py-3 tw-bg-geist-bg-2">
                  <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-geist-fg-2 tw-mb-1">Total payable amount</div>
                  <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1">
                    {[['Amount', formatCurrency(currency === 'USD' ? pr.total_usd : pr.total_vnd, currency)], ['VAT', formatCurrency(pr.total_vat, currency)], ['Total', formatCurrency(pr.total_amount, currency)]]
                      .map(([label, value]) => (
                        <div key={label} className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
                          <dt className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-geist-fg-3">{label}</dt>
                          <dd className="tw-m-0 tw-text-[12px] tw-tabular-nums tw-font-semibold tw-text-geist-fg">{value}</dd>
                        </div>
                      ))}
                  </dl>
                </div>
              </div>

              <div className="table-scroll tw-hidden md:tw-block tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse">
                  <thead>
                    <tr className="tw-border-b tw-border-solid tw-border-geist-border">
                      <th className={`${TH} tw-w-8`} />
                      <th className={TH}>Inv date</th>
                      <th className={TH}>Inv No</th>
                      <th className={TH}>Description</th>
                      <th className={TH}>Cost center</th>
                      <th className={`${TH} tw-text-right`}>Amount</th>
                      <th className={`${TH} tw-text-right`}>VAT</th>
                      <th className={`${TH} tw-text-right`}>Total</th>
                      <th className={TH}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pr.items || []).length === 0 ? (
                      <tr><td colSpan={9} className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-geist-fg-3 tw-italic">No invoice lines.</td></tr>
                    ) : pr.items.map((i) => (
                      <tr key={i.id}
                        className={`tw-border-b tw-border-solid tw-border-geist-border last:tw-border-0 ${
                          previewDocId && previewDocId === i.doc_platform_document_id ? "tw-bg-geist-accent/5" : "hover:tw-bg-geist-bg-2"
                        }`}>
                        <td className="tw-px-2 tw-py-2 tw-text-center tw-align-top">
                          {i.doc_platform_document_id ? (
                            <Clickable
                              onClick={() => showPreview(i.doc_platform_document_id)}
                              title={`View invoice PDF${i.inv_no ? ` ${i.inv_no}` : ""}`}
                              className="tw-inline-flex tw-text-geist-fg-3 hover:tw-text-geist-accent tw-transition-colors"
                            >
                              <i className="bi bi-file-earmark-pdf tw-text-[13px]" />
                            </Clickable>
                          ) : (
                            <span className="tw-text-geist-fg-3 tw-text-[11px]" title="No linked invoice file">—</span>
                          )}
                        </td>
                        <td className={`${TD} tw-whitespace-nowrap`}>{i.inv_date}</td>
                        <td className={`${TD} tw-whitespace-nowrap`}>{i.inv_no}</td>
                        <td className={`${TD} tw-min-w-[240px]`}>{i.description}</td>
                        <td className={TD}>{i.cost_center}</td>
                        <td className={`${TD} ${NUM} tw-whitespace-nowrap`}>{formatCurrency(lineAmount(i, currency), currency)}</td>
                        <td className={`${TD} ${NUM} tw-whitespace-nowrap`}>{formatCurrency(i.vat, currency)}</td>
                        <td className={`${TD} ${NUM} tw-font-semibold tw-whitespace-nowrap`}>{formatCurrency(i.total, currency)}</td>
                        <td className={TD}>{i.note}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="tw-bg-geist-bg-2 tw-font-semibold tw-text-geist-fg tw-border-t tw-border-solid tw-border-geist-border">
                      <td className="tw-px-4 tw-py-2 tw-text-right tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-geist-fg-2 tw-whitespace-nowrap" colSpan={5}>Total payable amount</td>
                      <td className={`${TD} ${NUM} tw-whitespace-nowrap`}>{formatCurrency(currency === "USD" ? pr.total_usd : pr.total_vnd, currency)}</td>
                      <td className={`${TD} ${NUM} tw-whitespace-nowrap`}>{formatCurrency(pr.total_vat, currency)}</td>
                      <td className={`${TD} ${NUM} tw-whitespace-nowrap`}>{formatCurrency(pr.total_amount, currency)}</td>
                      <td className={TD}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Purchase Order — căn cứ dựng Journal. Phải link PO trước khi
                tạo Journal, nên card này đứng TRƯỚC card ERP & ngân hàng. */}
            <PurchaseOrderCard paymentRequest={pr} />

            {/* ERP & ngân hàng — trạng thái tách khỏi trạng thái duyệt. Ai xem được tab
                ERP journals thì thấy nó ở đó; người còn lại vẫn thấy ở đây. */}
            {!perms.allow_view_journal && <IntegrationsCard paymentRequest={pr} />}

            {/* Người ký cuối — Kế toán trưởng chọn, mặc định là người ký thường xuyên. */}
            <FinalSignerCard paymentRequest={pr} />

            {/* Hóa đơn Doc Search của phiếu; file tự tải lên hiện riêng ở Attachments. */}
            <DocumentsTable
              documents={(pr.documents || []).filter((d) => d.source !== "upload")}
              readOnly
              activeId={previewDocId}
              onPreview={(doc) => {
                const docId = doc.document_id || doc.doc_platform_document_id;
                if (docId) showPreview(docId);
              }}
            />

            {(pr.documents || []).some((d) => d.source === "upload" && d.kind !== "accounting") && (
              <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-p-5">
                <AttachmentsPanel attachments={(pr.documents || []).filter((d) => d.source === "upload" && d.kind !== "accounting")} readOnly />
              </div>
            )}

            {/* Files Accounting adds while completing the request (moved here from the Journal tab). */}
            {(perms.allow_refill_journal || perms.allow_finance_insight) && (
              <AccountingAttachments paymentRequest={pr} editable={!!perms.allow_refill_journal} />
            )}


            {/* Xem lại PDF hoá đơn — panel dính dưới bảng trên desktop, mobile mở overlay */}
            {previewDocId && (
              <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
                <div className="tw-px-5 tw-py-2.5 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-flex tw-items-center tw-gap-2">
                  <i className="bi bi-file-earmark-pdf tw-text-geist-fg-3" />
                  <h3 className="tw-text-[13px] tw-font-semibold tw-text-geist-fg tw-m-0 tw-flex-1 tw-truncate">
                    Invoice preview
                    {activeInvoiceNo && <span className="tw-font-normal tw-text-geist-fg-3"> · {activeInvoiceNo}</span>}
                  </h3>
                  <Clickable onClick={() => setPdfOpen(true)} title="Open full screen"
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-text-geist-accent hover:tw-underline">
                    <i className="bi bi-arrows-fullscreen" /> <span className="lg:tw-inline tw-hidden">Full screen</span><span className="lg:tw-hidden">View PDF</span>
                  </Clickable>
                  <Clickable onClick={() => { setPreviewDocId(null); setPreviewUrl(null); }} title="Close preview"
                    className="tw-inline-flex tw-text-geist-fg-3 hover:tw-text-geist-fg">
                    <i className="bi bi-x-lg tw-text-[11px]" />
                  </Clickable>
                </div>
                {/* Trên mobile không nhúng iframe PDF (bị co lại không đọc được) — dùng overlay. */}
                <div className="tw-hidden lg:tw-block tw-h-[560px] tw-bg-geist-bg-2">
                  <InvoicePreviewFrame url={previewUrl} loading={previewLoading} />
                </div>
                <div className="lg:tw-hidden tw-px-5 tw-py-4 tw-text-center tw-text-[12px] tw-text-geist-fg-3">
                  Tap “View PDF” to open the invoice full screen.
                </div>
              </div>
            )}


            {/* Clear downpayment (qf03) */}
            {pr.form_type === "qf03" && (
              <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
                <div className="tw-px-5 tw-py-2.5 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2 tw-flex tw-items-center tw-gap-2">
                  <i className="bi bi-arrow-left-right tw-text-geist-fg-3" />
                  <h3 className="tw-text-[13px] tw-font-semibold tw-text-geist-fg tw-m-0">Clear downpayment</h3>
                </div>
                <div className="tw-p-5">
                  <Info label="Advance request">{pr.parent_advance
                    ? `${pr.parent_advance.code} · ${pr.parent_advance.payee || ""}`
                    : pr.legacy_advance_ref ? `${pr.legacy_advance_ref}${pr.legacy_advance_date ? ` · ${formatDate(pr.legacy_advance_date)}` : ""} · not in the Portal` : null}</Info>
                  <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-3">
                    <PaperStat label="Total cost" value={pr.total_vnd} />
                    <PaperStat label="Advanced" value={pr.advanced_amount_vnd} />
                    <PaperStat
                      label={Number(pr.payable_receivable_vnd) >= 0 ? "Payable" : "Receivable"}
                      value={Math.abs(Number(pr.payable_receivable_vnd) || 0)}
                      tone={Number(pr.payable_receivable_vnd) >= 0 ? "danger" : "success"}
                    />
                  </div>
                </div>
              </div>
            )}
            </>)}

            {/* Every recorded step of the request, for tracking. */}
            {/* Checking tools are for Accounting / admins — approvers see the plain request. */}
            {pr.id && pr.status?.id !== 0 && perms.allow_finance_insight && <ActivityTimeline paymentRequest={pr} />}
          </div>
          )}
          right={(
          /* Right column — step-by-step timeline */
          <ApprovalProgress
            approvals={pr.approvals || []}
            preview={pr.approval_preview}
            requestor={pr.requestor}
            statusId={statusId}
            disbursement={pr.disbursement}
            disbursementAction={(
              <DisbursementAction canMarkPaid={!!perms.allow_mark_disbursement_paid} onMarkPaid={() => setShowMarkPaid(true)}
                hsbc={canSendHsbc} onOpenBank={openBankCard} />
            )}
            className="lg:tw-sticky lg:tw-top-4"
          />
          )} />
      </div>

      <Modal show={showFinanceDocuments} onHide={() => setShowFinanceDocuments(false)} title="Finance workflow & documents" size="md">
        {showFinanceDocuments && <FinanceCompletionCard paymentRequest={pr} embedded />}
      </Modal>
      <PdfOverlay show={pdfOpen} url={previewUrl} loading={previewLoading} onClose={() => setPdfOpen(false)} />

      <MarkPaidModal show={showMarkPaid} onHide={() => setShowMarkPaid(false)} paymentRequest={pr} />
      <ApproveForm show={showApprove} onHide={() => setShowApprove(false)} paymentRequest={pr}
        onPrepareJournal={perms.allow_refill_journal ? prepareJournal : undefined} />
      <RejectForm  show={showReject}  onHide={() => setShowReject(false)}  paymentRequest={pr} />
      <CancelForm  show={showCancel}  onHide={() => setShowCancel(false)}  paymentRequest={pr} />
      <Modal show={showDelete} onHide={() => setShowDelete(false)} title={`Delete ${pr.code}`} size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" disabled={busyAction === "delete"} onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button variant="danger"    size="sm" leftIcon={FaTrash} onClick={doDelete}
              loading={busyAction === "delete"} aria-busy={busyAction === "delete"}>
              {busyAction === "delete" ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p className="tw-text-sm tw-text-geist-fg-2 tw-m-0">Are you sure you want to delete this Payment Requests? This cannot be undone.</p>
      </Modal>
    </PageShell>
  );
};

export default PaymentRequestDetail;
